import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { usePricing } from '../context/PricingContext';
import { supabase } from '../lib/supabaseClient';
import SEO from '../components/SEO';
import SubjectCard from '../components/SubjectCard';
import CourseInfoBanner from '../components/CourseInfoBanner';
import { BreadcrumbSchema, CourseListSchema } from '../components/StructuredData';

const ALevelsPage = () => {
  const [asSubjects, setAsSubjects] = useState([]);
  const [a2Subjects, setA2Subjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const pricing = usePricing();

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const [asRes, a2Res] = await Promise.all([
          supabase.from('subjects').select('*').eq('level', 'AS'),
          supabase.from('subjects').select('*').eq('level', 'A2')
        ]);
        if (asRes.data) setAsSubjects(asRes.data);
        if (a2Res.data) setA2Subjects(a2Res.data);
      } catch (e) {
        console.error('Error fetching subjects:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchSubjects();
  }, []);

  const isLocal = pricing.countryCode === 'LK';

  const getGroupPrice = (subject) => {
    return isLocal ? (subject.group_price_local || 2500) : (subject.group_price_international || 4000);
  };

  // Price for the info banner — uses the first subject in the list since all
  // subjects at a given level share the same price.
  const asBannerPrice = asSubjects[0] ? getGroupPrice(asSubjects[0]) : (isLocal ? 2500 : 4000);
  const a2BannerPrice = a2Subjects[0] ? getGroupPrice(a2Subjects[0]) : (isLocal ? 2500 : 4000);

  return (
    <main data-testid="alevels-page">
      <SEO
        title="A Level Programs"
        description="Explore AS and A2 Level tuition at Aspire Academy. Edexcel International curriculum, expert tutors, university preparation. Mathematics, Sciences, Commerce, and more."
        path="/a-levels"
      />
      <BreadcrumbSchema items={[{ name: 'Home', href: '/' }, { name: 'A Level Programs' }]} />
      <CourseListSchema courses={[...asSubjects, ...a2Subjects]} level="A Level" />
      {/* Hero */}
      <section className="bg-surface py-16 md:py-24">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="label-uppercase text-aspire-red">Advanced Level</span>
            <h1 className="heading-hero mt-2 mb-6">
              A Level <span className="text-aspire-red">Programs</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Advanced level courses following the Edexcel International curriculum. 
              Our comprehensive AS and A2 programs prepare students for top universities worldwide 
              with rigorous academic training and exam-focused strategies.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 text-sm font-medium">
              First Demo Class FREE - Try before you commit!
            </div>
          </div>
        </div>
      </section>

      {/* Level Toggle & Subjects Grid */}
      <section className="section-spacing bg-white">
        <div className="container-main">
          <Tabs defaultValue="as" className="w-full">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
              <div>
                <h2 className="heading-section">A Level Subjects</h2>
                <p className="text-gray-600 mt-2">Toggle between AS and A2 levels</p>
              </div>
              <TabsList className="bg-gray-100" data-testid="level-tabs">
                <TabsTrigger value="as" className="data-[state=active]:bg-aspire-red data-[state=active]:text-white px-4 sm:px-8" data-testid="as-tab">
                  AS Level
                </TabsTrigger>
                <TabsTrigger value="a2" className="data-[state=active]:bg-aspire-red data-[state=active]:text-white px-4 sm:px-8" data-testid="a2-tab">
                  A2 Level
                </TabsTrigger>
              </TabsList>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aspire-red mx-auto"></div>
              </div>
            ) : (
              <>
                <TabsContent value="as">
                  <CourseInfoBanner
                    price={asBannerPrice}
                    duration="2 hours"
                    maxStudents={8}
                    targetGrade="A* - B"
                    examBoard="Edexcel"
                    context="All AS Level subjects · Edexcel International curriculum"
                  />
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {asSubjects.map((subject) => (
                      <SubjectCard
                        key={subject.subject_id}
                        subject={subject}
                        to={`/a-levels/${subject.slug}`}
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="a2">
                  <CourseInfoBanner
                    price={a2BannerPrice}
                    duration="2 hours"
                    maxStudents={8}
                    targetGrade="A* - B"
                    examBoard="Edexcel"
                    context="All A2 Level subjects · Edexcel International curriculum"
                  />
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {a2Subjects.map((subject) => (
                      <SubjectCard
                        key={subject.subject_id}
                        subject={subject}
                        to={`/a-levels/${subject.slug}`}
                      />
                    ))}
                  </div>
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </section>

      {/* Progression Info */}
      <section className="section-spacing bg-charcoal">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="label-uppercase text-amber-500">Study Pathway</span>
            <h2 className="heading-section mt-2 text-white">AS to A2 Progression</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/5 backdrop-blur p-4 sm:p-8">
              <div className="w-16 h-16 bg-aspire-red flex items-center justify-center mb-6">
                <span className="font-heading font-bold text-xl text-white">AS</span>
              </div>
              <h3 className="font-heading font-bold text-xl text-white mb-4">AS Level (Year 1)</h3>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li>• Foundation of A Level content</li>
                <li>• 50% of final A Level qualification</li>
                <li>• Can be taken as standalone qualification</li>
                <li>• Examination: May/June session</li>
              </ul>
            </div>
            <div className="bg-white/5 backdrop-blur p-4 sm:p-8">
              <div className="w-16 h-16 bg-charcoal border-2 border-white flex items-center justify-center mb-6">
                <span className="font-heading font-bold text-xl text-white">A2</span>
              </div>
              <h3 className="font-heading font-bold text-xl text-white mb-4">A2 Level (Year 2)</h3>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li>• Advanced content building on AS</li>
                <li>• Completes full A Level qualification</li>
                <li>• Required for top university admission</li>
                <li>• Examination: May/June session</li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-400 max-w-2xl mx-auto">
              Students typically complete AS Level in Year 12 and progress to A2 Level in Year 13. 
              Both levels together form the complete GCE A Level qualification required for university admission.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ALevelsPage;
